import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  Shield, 
  Activity, 
  BarChart3, 
  TestTube,
  Eye,
  MessageSquare,
  PieChart,
  Database,
  Settings,
  ArrowRight,
  CheckCircle,
  AlertTriangle,
  XCircle
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const monitoringTools = [
  {
    id: 'sentry',
    name: 'Sentry',
    nameAr: 'سنتري',
    description: 'تتبع الأخطاء ومراقبة الأداء',
    icon: Shield,
    category: 'monitoring',
    status: 'active',
    path: '/monitoring/sentry',
    color: 'text-purple-600',
    bgColor: 'bg-purple-50',
    metrics: { errors: 12, performance: 95 }
  },
  {
    id: 'logrocket',
    name: 'LogRocket',
    nameAr: 'لوج روكيت',
    description: 'تسجيل وإعادة تشغيل الجلسات',
    icon: Eye,
    category: 'monitoring',
    status: 'active',
    path: '/monitoring/logrocket',
    color: 'text-blue-600',
    bgColor: 'bg-blue-50',
    metrics: { sessions: 245, recordings: 89 }
  },
  {
    id: 'mixpanel',
    name: 'Mixpanel',
    nameAr: 'ميكس بانل',
    description: 'تحليلات المستخدمين وتتبع السلوك',
    icon: PieChart,
    category: 'monitoring',
    status: 'active',
    path: '/monitoring/mixpanel',
    color: 'text-green-600',
    bgColor: 'bg-green-50',
    metrics: { events: 15420, users: 1250 }
  },
  {
    id: 'newrelic',
    name: 'New Relic',
    nameAr: 'نيو ريليك',
    description: 'مراقبة الأداء وتحليل البنية التحتية',
    icon: Activity,
    category: 'monitoring',
    status: 'active',
    path: '/monitoring/newrelic',
    color: 'text-teal-600',
    bgColor: 'bg-teal-50',
    metrics: { uptime: 99.9, response: 120 }
  },
  {
    id: 'datadog',
    name: 'DataDog',
    nameAr: 'داتا دوغ',
    description: 'مراقبة شاملة للبنية التحتية والتطبيقات',
    icon: Database,
    category: 'monitoring',
    status: 'warning',
    path: '/monitoring/datadog',
    color: 'text-orange-600',
    bgColor: 'bg-orange-50',
    metrics: { alerts: 3, dashboards: 12 }
  }
];

const testingTools = [
  {
    id: 'jest',
    name: 'Jest',
    nameAr: 'جيست',
    description: 'اختبارات الوحدة والتكامل',
    icon: TestTube,
    category: 'testing',
    status: 'active',
    path: '/monitoring/jest',
    color: 'text-red-600',
    bgColor: 'bg-red-50',
    metrics: { tests: 187, coverage: 85 }
  },
  {
    id: 'cypress',
    name: 'Cypress',
    nameAr: 'سايبرس',
    description: 'اختبارات شاملة للواجهات',
    icon: Activity,
    category: 'testing',
    status: 'active',
    path: '/monitoring/cypress',
    color: 'text-emerald-600',
    bgColor: 'bg-emerald-50',
    metrics: { tests: 45, passed: 42 }
  },
  {
    id: 'storybook',
    name: 'Storybook',
    nameAr: 'ستوري بوك',
    description: 'اختبار وتطوير المكونات',
    icon: BarChart3,
    category: 'testing',
    status: 'inactive',
    path: '/monitoring/storybook',
    color: 'text-pink-600',
    bgColor: 'bg-pink-50',
    metrics: { stories: 78, components: 34 }
  },
  {
    id: 'lighthouse',
    name: 'Lighthouse',
    nameAr: 'لايت هاوس',
    description: 'اختبار الأداء وتحسين الويب',
    icon: MessageSquare,
    category: 'testing',
    status: 'active',
    path: '/monitoring/lighthouse',
    color: 'text-indigo-600',
    bgColor: 'bg-indigo-50',
    metrics: { performance: 92, accessibility: 96 }
  }
];

const getStatusIcon = (status: string) => {
  switch (status) {
    case 'active':
      return <CheckCircle className="h-4 w-4 text-green-500" />;
    case 'warning':
      return <AlertTriangle className="h-4 w-4 text-yellow-500" />;
    case 'inactive':
      return <XCircle className="h-4 w-4 text-red-500" />;
    default:
      return null;
  }
};

const getStatusText = (status: string) => {
  switch (status) {
    case 'active':
      return 'نشط';
    case 'warning':
      return 'تحذير';
    case 'inactive':
      return 'متوقف';
    default:
      return 'غير معروف';
  }
};

export default function SystemMonitoring() {
  const navigate = useNavigate();
  const [selectedCategory, setSelectedCategory] = useState<'all' | 'monitoring' | 'testing'>('all');
  
  const allTools = [...monitoringTools, ...testingTools];
  const filteredTools = selectedCategory === 'all' 
    ? allTools 
    : allTools.filter(tool => tool.category === selectedCategory);

  const activeCount = allTools.filter(tool => tool.status === 'active').length;
  const warningCount = allTools.filter(tool => tool.status === 'warning').length;
  const inactiveCount = allTools.filter(tool => tool.status === 'inactive').length;

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* العنوان الرئيسي */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">مراقبة النظام</h1>
          <p className="text-muted-foreground">
            لوحة تحكم شاملة لمراقبة الأدوات والاختبارات
          </p>
        </div>
        <Button onClick={() => navigate('/system-health')} variant="outline">
          <Settings className="h-4 w-4 ml-2" />
          إعدادات النظام
        </Button>
      </div>

      {/* إحصائيات سريعة */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <CheckCircle className="h-8 w-8 text-green-500" />
              <div>
                <p className="text-2xl font-bold">{activeCount}</p>
                <p className="text-sm text-muted-foreground">نشط</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <AlertTriangle className="h-8 w-8 text-yellow-500" />
              <div>
                <p className="text-2xl font-bold">{warningCount}</p>
                <p className="text-sm text-muted-foreground">تحذير</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <XCircle className="h-8 w-8 text-red-500" />
              <div>
                <p className="text-2xl font-bold">{inactiveCount}</p>
                <p className="text-sm text-muted-foreground">متوقف</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <BarChart3 className="h-8 w-8 text-blue-500" />
              <div>
                <p className="text-2xl font-bold">{allTools.length}</p>
                <p className="text-sm text-muted-foreground">إجمالي الأدوات</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* فلاتر الفئات */}
      <div className="flex gap-2">
        <Button 
          variant={selectedCategory === 'all' ? 'default' : 'outline'}
          onClick={() => setSelectedCategory('all')}
        >
          جميع الأدوات
        </Button>
        <Button 
          variant={selectedCategory === 'monitoring' ? 'default' : 'outline'}
          onClick={() => setSelectedCategory('monitoring')}
        >
          أدوات المراقبة
        </Button>
        <Button 
          variant={selectedCategory === 'testing' ? 'default' : 'outline'}
          onClick={() => setSelectedCategory('testing')}
        >
          أدوات الاختبار
        </Button>
      </div>

      {/* شبكة الأدوات */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredTools.map((tool) => {
          const IconComponent = tool.icon;
          return (
            <Card key={tool.id} className="hover:shadow-lg transition-shadow cursor-pointer">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div className={`p-3 rounded-lg ${tool.bgColor}`}>
                    <IconComponent className={`h-6 w-6 ${tool.color}`} />
                  </div>
                  <div className="flex items-center gap-2">
                    {getStatusIcon(tool.status)}
                    <Badge variant="secondary">
                      {getStatusText(tool.status)}
                    </Badge>
                  </div>
                </div>
                <CardTitle className="text-lg">{tool.nameAr}</CardTitle>
                <CardDescription>{tool.description}</CardDescription>
              </CardHeader>
              
              <CardContent className="pt-0">
                <div className="space-y-3">
                  {/* عرض المقاييس */}
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    {Object.entries(tool.metrics).map(([key, value]) => (
                      <div key={key} className="text-center p-2 bg-muted rounded">
                        <p className="font-semibold">{value}</p>
                        <p className="text-muted-foreground text-xs">
                          {key === 'errors' && 'أخطاء'}
                          {key === 'performance' && 'الأداء%'}
                          {key === 'sessions' && 'جلسات'}
                          {key === 'recordings' && 'تسجيلات'}
                          {key === 'events' && 'أحداث'}
                          {key === 'users' && 'مستخدمين'}
                          {key === 'uptime' && 'وقت التشغيل%'}
                          {key === 'response' && 'الاستجابة (ms)'}
                          {key === 'alerts' && 'تنبيهات'}
                          {key === 'dashboards' && 'لوحات'}
                          {key === 'tests' && 'اختبارات'}
                          {key === 'coverage' && 'التغطية%'}
                          {key === 'passed' && 'نجح'}
                          {key === 'stories' && 'قصص'}
                          {key === 'components' && 'مكونات'}
                          {key === 'accessibility' && 'إمكانية الوصول%'}
                        </p>
                      </div>
                    ))}
                  </div>
                  
                  <Button 
                    className="w-full" 
                    onClick={() => navigate(tool.path)}
                  >
                    عرض التفاصيل
                    <ArrowRight className="h-4 w-4 mr-2" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}